import { useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDownAZ, ArrowUpAZ, Check, FileText, GripVertical, ImageIcon, ListOrdered, Pencil, RotateCcw, X } from "lucide-react";
import { splitExt, validateBaseName } from "../lib/filelist";

function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function SortableRow({ entry, index, prefix, canDrag, disabled, renaming, onStartRename, onCancelRename, onCommitRename, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: entry.id,
        disabled: !canDrag || disabled,
    });
    const [draft, setDraft] = useState("");
    const [renameError, setRenameError] = useState(null);
    const { base, ext } = splitExt(entry.name);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        position: isDragging ? "relative" : undefined,
    };

    const commit = () => {
        const err = validateBaseName(draft);
        if (err) {
            setRenameError(err);
            return;
        }
        onCommitRename(entry.id, draft);
        setRenameError(null);
    };

    return (
        <li
            ref={setNodeRef}
            style={style}
            data-testid={`${prefix}file-item-${index}`}
            className={`flex items-center gap-2 px-3 py-2.5 bg-[#FAFAF9] ${isDragging ? "shadow-lg border-2 border-[#0284C7] bg-white" : ""}`}
        >
            <button
                type="button"
                {...attributes}
                {...listeners}
                disabled={!canDrag || disabled}
                aria-label={`Arrastar ${entry.name}`}
                data-testid={`${prefix}drag-${index}`}
                title={canDrag ? "Arraste para reorganizar" : "Mude para 'Ordem manual' para arrastar"}
                className={`p-1.5 touch-none select-none ${
                    canDrag && !disabled
                        ? "cursor-grab active:cursor-grabbing text-gray-600 hover:text-black hover:bg-gray-200"
                        : "text-gray-300 cursor-not-allowed"
                } transition-colors`}
            >
                <GripVertical className="w-4 h-4" aria-hidden="true" />
            </button>
            {entry.type === "application/pdf" || entry.name.toLowerCase().endsWith(".pdf") ? (
                <FileText className="w-5 h-5 text-[#0284C7] flex-shrink-0" aria-hidden="true" />
            ) : (
                <ImageIcon className="w-5 h-5 text-[#0284C7] flex-shrink-0" aria-hidden="true" />
            )}
            {renaming ? (
                <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1">
                        <input
                            autoFocus
                            value={draft}
                            onChange={(e) => { setDraft(e.target.value); setRenameError(null); }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") commit();
                                if (e.key === "Escape") { setRenameError(null); onCancelRename(); }
                            }}
                            aria-label={`Novo nome para ${entry.name}`}
                            data-testid={`${prefix}rename-input-${index}`}
                            className="flex-1 min-w-0 border-2 border-black bg-white px-2 py-1 text-sm font-medium"
                        />
                        {ext && <span className="font-mono-tag text-xs text-gray-500 flex-shrink-0">{ext}</span>}
                        <button
                            type="button"
                            onClick={commit}
                            aria-label="Confirmar nome"
                            data-testid={`${prefix}rename-save-${index}`}
                            className="p-1.5 bg-[#0284C7] text-white hover:bg-black transition-colors"
                        >
                            <Check className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => { setRenameError(null); onCancelRename(); }}
                            aria-label="Cancelar renomeação"
                            data-testid={`${prefix}rename-cancel-${index}`}
                            className="p-1.5 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </span>
                    {renameError && (
                        <span className="block mt-1 text-xs text-red-700 font-medium" role="alert" data-testid={`${prefix}rename-error-${index}`}>
                            {renameError}
                        </span>
                    )}
                </span>
            ) : (
                <>
                    <span className="text-sm font-medium text-gray-900 truncate flex-1 min-w-0">{entry.name}</span>
                    <span className="font-mono-tag text-xs text-gray-500 flex-shrink-0">{formatBytes(entry.size)}</span>
                    {!disabled && (
                        <>
                            <button
                                type="button"
                                onClick={() => { setDraft(base); setRenameError(null); onStartRename(entry.id); }}
                                aria-label={`Renomear ${entry.name}`}
                                data-testid={`${prefix}rename-${index}`}
                                className="p-1.5 hover:bg-black hover:text-white transition-colors"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => onRemove(entry.id)}
                                aria-label={`Remover ${entry.name}`}
                                data-testid={`${prefix}remove-file-${index}`}
                                className="p-1.5 hover:bg-black hover:text-white transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </>
                    )}
                </>
            )}
        </li>
    );
}

/**
 * Lista de arquivos com ordenacao A-Z/Z-A, renomeacao e arrastar-soltar
 * (mouse, toque e teclado). A ordem exibida e a ordem enviada ao backend.
 */
export function FileSortList({ fl, disabled = false, prefix = "" }) {
    const [renamingId, setRenamingId] = useState(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const canDrag = fl.sortMode === "manual" && !disabled;

    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const ids = fl.displayed.map((e) => e.id);
        const from = ids.indexOf(active.id);
        const to = ids.indexOf(over.id);
        if (from < 0 || to < 0) return;
        const next = [...ids];
        next.splice(to, 0, next.splice(from, 1)[0]);
        fl.reorder(next);
    };

    const sortBtn = (mode, label, Icon, testid) => (
        <button
            type="button"
            onClick={() => fl.setSortMode(mode)}
            aria-pressed={fl.sortMode === mode}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-black font-mono-tag text-[11px] font-bold uppercase transition-colors ${
                fl.sortMode === mode ? "bg-black text-[#22D3EE]" : "bg-white text-black hover:bg-[#F0F9FF]"
            }`}
        >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {label}
        </button>
    );

    return (
        <div className="mx-4 mb-4" data-testid={`${prefix}file-sort-list`}>
            {fl.entries.length > 1 && (
                <div className="mb-2 flex items-center gap-2 flex-wrap" role="group" aria-label="Ordenação dos arquivos">
                    {sortBtn("asc", "Nome A–Z", ArrowDownAZ, `${prefix}sort-asc`)}
                    {sortBtn("desc", "Nome Z–A", ArrowUpAZ, `${prefix}sort-desc`)}
                    {sortBtn("manual", "Ordem manual", ListOrdered, `${prefix}sort-manual`)}
                    {fl.sortMode === "manual" && !disabled && (
                        <button
                            type="button"
                            onClick={fl.restoreOriginal}
                            data-testid={`${prefix}sort-restore`}
                            title="Restaurar a ordem em que os arquivos foram enviados"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono-tag text-[11px] font-bold uppercase text-gray-500 underline decoration-[#0284C7] underline-offset-2 hover:text-[#0284C7]"
                        >
                            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Ordem de envio
                        </button>
                    )}
                </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={fl.displayed.map((e) => e.id)} strategy={verticalListSortingStrategy}>
                    <ul className="divide-y divide-gray-200 border-2 border-black" data-testid={`${prefix}file-list`}>
                        {fl.displayed.map((entry, i) => (
                            <SortableRow
                                key={entry.id}
                                entry={entry}
                                index={i}
                                prefix={prefix}
                                canDrag={canDrag}
                                disabled={disabled}
                                renaming={renamingId === entry.id}
                                onStartRename={setRenamingId}
                                onCancelRename={() => setRenamingId(null)}
                                onCommitRename={(id, base) => {
                                    const err = fl.rename(id, base);
                                    if (!err) setRenamingId(null);
                                }}
                                onRemove={fl.remove}
                            />
                        ))}
                    </ul>
                </SortableContext>
            </DndContext>
            {fl.sortMode !== "manual" && fl.entries.length > 1 && (
                <p className="mt-1.5 font-mono-tag text-[11px] text-gray-500" data-testid={`${prefix}sort-hint`}>
                    Ordenado por nome. Para arrastar, mude para “Ordem manual”.
                </p>
            )}
        </div>
    );
}
